@use 'sass:map';
@use '../../scss/global' as *;
@use 'themes';

// ================================================================ //
// -- Local Variables

/**
 * Offset for focus state outline (i.e. "focus ring").
 */
$outline-offset: 4px;

/**
 * Mimics user agent outline (i.e. "focus ring") styles. Used to
 * apply focus state to <input> element parent container.
 */
%pseudo-focus-ring {
  outline-style: auto;
  outline-width: 2px;
  outline-color: Highlight;

  @media (-webkit-min-device-pixel-ratio: 0) {
    outline-color: -webkit-focus-ring-color;
  }
}

// ================================================================ //
// -- Component Styles

:host {
  display: block;
  vertical-align: middle; // Vertically center when "display: inline-block"

  .fluid-toggle {
    display: flex;
    align-items: center;
    line-height: 1;
    color: var(--I-color-text, var(--G-color-text));
    font-weight: var(--I-font-weight, var(--G-font-weight));

    // ==== Toggle Label (Container)

    .toggle-label {
      flex: 0 1 auto;
      max-width: 100%;
      display: inline-flex;
      align-items: flex-start;
      column-gap: var(--label-spacing);
      position: relative;
      vertical-align: middle;
      line-height: var(--I-label-line-height, var(--label-line-height));
      padding-top: var(--label-padding-top);
      padding-bottom: var(--label-padding-bottom);
      cursor: pointer;

      // == Flag Toggle

      &.flag-toggle {
        fluid-icon {
          color: $ui-gray;
        }
      }

      // == Toggle Switch

      &.toggle-switch {
        align-items: center;
        padding-top: var(--switch-padding-top);
        padding-bottom: var(--switch-padding-bottom);
      }

      // == Menu Item (Context Menu)

      &.menu-item {
        flex: 1 1 auto;
        align-items: center;
        line-height: var(--G-menu-item-line-height);
        padding-top: var(--G-menu-item-padding-top);
        padding-bottom: var(--G-menu-item-padding-bottom);
        padding-left: var(--G-menu-item-padding-left);
        padding-right: var(--G-menu-item-padding-right);

        @include transition(
          $properties: 'background-color',
          $duration: short
        );

        // == Hover & Focus States

        &:hover,
        &:has(.toggle-input:focus-visible) {
          background-color: var(--G-menu-item-background-hover);
        }

        .toggle-container,
        .toggle-switch-container {
          outline: none;
        }
      }

      // == Block Layout

      &.block-layout {
        flex: 1 1 auto;
      }

      // == Remove Padding

      &.no-padding {
        padding-top: 0;
        padding-bottom: 0;
      }

      // == Readonly State

      &.read-only {
        pointer-events: none;
      }

      // == Disabled State

      &.disabled {
        cursor: not-allowed;

        &:not(.chip-toggle) {
          .toggle-label-text,
          .info-popover {
            color: var(--G-form-element-color-disabled);
          }
        }
      }
    }

    // ==== Chip Toggle

    .chip-toggle {
      min-width: var(--chip-height);
      height: var(--chip-height);
      justify-content: center;
      align-items: center;
      line-height: 1em;
      color: var(--chip-color);
      font-weight: var(--I-font-weight, $font-weight-medium);
      text-align: center;
      padding-top: 0;
      padding-bottom: 0;
      padding-left: 0.6em;
      padding-right: 0.6em;
      margin-top: 0.375em;
      margin-bottom: 0.375em;
      background-color: var(--chip-background-color);
      border-width: var(--G-border-width);
      border-style: solid;
      border-color: var(--chip-border-color);
      border-radius: calc(var(--chip-height) / 2);

      @include transition(
        $properties: 'color, background-color, border-color',
        $duration: rapid
      );

      // == "Active" State

      &:has(.toggle-input:active) {
        transition: initial; // "Pressed state" transition begins immediately
      }

      // == Disabled State

      &.disabled {
        font-weight: $font-weight-regular;
      }

      // == Remove Padding

      &.no-padding {
        margin-top: 0;
        margin-bottom: 0;
      }

      // == Internal Elements

      .toggle-container {
        outline: none;
      }

      .toggle-input {
        width: var(--chip-font-size);
        height: var(--chip-font-size);
        accent-color: $ui-gray;
      }

      .toggle-label-text {
        font-size: var(--chip-font-size);
        font-weight: inherit;
        text-align: inherit;
        user-select: none;
      }
    }

    // ==== Checkbox Input Container

    .toggle-container {
      flex: 0 0 auto;
      min-width: 1em;
      height: 1lh;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      outline-offset: $outline-offset;

      // == Focus State

      // ":where" pseudo-class is used to avoid specificity issues
      // https://www.w3.org/TR/selectors-4/#zero-matches
      &:where(:has(.toggle-input:focus-visible)) {
        @extend %pseudo-focus-ring;
      }
    }

    // ==== Switch Container

    .toggle-switch-container {
      min-width: var(--switch-min-width);
      height: var(--switch-height);
      display: inline-flex;
      align-items: center;
      column-gap: 0.25em;
      position: relative;
      overflow: hidden;
      isolation: isolate; // Isolate "stacking context" to prevent z-index conflicts
      padding-left: calc(var(--switch-height) / 4);
      padding-right: calc(var(--switch-height) / 4);
      background-color: var(--switch-background-color);
      border-radius: calc(var(--switch-height) / 2);
      outline-offset: $outline-offset;
      box-shadow:
        inset 1px 1px 1px -1px rgba(0, 0, 0, .1),
        inset 1px 2px 2px 0px rgba(0, 0, 0, .08);

      @include transition(
        $properties: 'background-color',
        $duration: rapid
      );

      // == Slider

      &::before {
        width: var(--switch-height);
        height: var(--switch-height);
        position: absolute;
        top: 0;
        left: 0;
        background-color: $white;
        border-width: 2px;
        border-style: solid;
        border-color: var(--slider-border-color);
        border-radius: calc(var(--switch-height) / 2);
        content: "";
        z-index: 1;
        box-shadow:
          1px 1px 1px -1px rgba(0, 0, 0, .1),
          1px 1px 2px 0px rgba(0, 0, 0, .08);

        will-change: transform;
        animation-name: slide-left;
        animation-duration: map.get($transition-durations, short);
        animation-timing-function: ease-in-out;

        @include transition(
          $properties: 'transform, border-color',
          $duration: rapid
        );
      }

      // == Focus State

      // ":where" pseudo-class is used to avoid specificity issues
      // https://www.w3.org/TR/selectors-4/#zero-matches
      &:where(:has(.toggle-input:focus-visible)) {
        @extend %pseudo-focus-ring;
      }

      // == Disabled State

      &.switch-disabled {
        cursor: not-allowed;

        @include disabled-state-overlay(
          $opacity: 0.4,
          $z-index: 2,
        );
      }

      // == On/Checked State

      &.toggle-checked {
        &::before {
          animation-name: slide-right;
          animation-fill-mode: forwards;
        }

        .toggle-switch-on {
          opacity: 1;
        }

        .toggle-switch-off {
          opacity: 0;
        }
      }
    }

    // ==== Toggle Switch Text / Icons

    .toggle-switch-on,
    .toggle-switch-off {
      flex: 1 1 50%;
      padding-left: 1px;
      padding-right: 1px;
      font-family: inherit;
      line-height: 1;
      font-size: var(--switch-font-size);
      font-weight: $font-weight-bold;
      text-align: center;
      white-space: nowrap;
      user-select: none;

      @include transition(
        $properties: 'opacity',
        $duration: short
      );
    }

    .toggle-switch-on {
      opacity: 0;
      color: var(--G-color-text-inverse);
    }

    .toggle-switch-off {
      opacity: 1;
      color: var(--G-color-text);
    }

    // ==== Toggle Input (Checkbox)

    .toggle-input {
      @include input-reset;
      width: 1em;
      height: 1em;
      cursor: pointer;

      &[disabled] {
        cursor: not-allowed;
      }

      // == Hidden Checkbox (Used for Switch & Custom Toggle)

      &.input-hidden {
        appearance: none;
        position: absolute;
        opacity: 0;
        overflow: hidden;
      }
    }

    // ==== Label Text (Within Label)

    .toggle-label-text {
      font-size: inherit;
      line-height: inherit;

      // == Text Alignment

      &.align-left {
        order: -1;
      }

      &.align-right {
        order: 0;
      }

      // == Text Overflow

      &.text-overflow-wrap {
        overflow-wrap: break-word;
        hyphens: auto;
      }

      &.text-overflow-ellipsis,
      &.text-overflow-clip {
        white-space: nowrap;
        overflow: hidden;
        position: relative;
      }

      &.text-overflow-ellipsis {
        text-overflow: ellipsis;

        &:has(.info-popover) {
          padding-right: 1em;
        }

        .info-popover {
          position: absolute;
          top: 0.1em;
          right: 0;
        }
      }

      &.text-overflow-clip {
        text-overflow: clip;
      }
    }

    // == "Helptext" Popover

    .info-popover {
      min-width: 1em;
      min-height: 1em;
      margin-left: 0.25em;
    }

    // == Embedded Components

    fluid-icon {
      min-width: 1em;
      min-height: 1em;
      vertical-align: middle;
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    fluid-link {
      pointer-events: none;
    }
  }
}
